import { useEffect, useId, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { productConfig } from "../config/product";
import { getLanguage } from "../i18n/languages";
import { useSession } from "../state/SessionProvider";
import { BrandMark } from "./BrandMark";
import { TranslationPreviewNotice } from "./TranslationPreviewNotice";

/**
 * Shared chrome: subtle Leave + Need urgent help.
 * Home carries the brand as the main heading (Item 8); other routes keep BrandMark.
 */
export function PageShell() {
  const { translate, languageCode } = useSession();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isScaffoldMode =
    location.pathname.startsWith("/pro/") &&
    location.pathname !== "/pro/hospitality";
  const lang = getLanguage(languageCode);
  const dir = lang?.direction ?? "ltr";
  const [urgentOpen, setUrgentOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    document.title = isHome
      ? productConfig.shortDescription
      : `${productConfig.displayName} — ${productConfig.shortDescription}`;
  }, [isHome]);

  // After route changes, move focus to the page h1 when present (and dialog closed).
  useEffect(() => {
    if (urgentOpen) return;
    const main = document.getElementById("main");
    if (!main) return;
    const heading = main.querySelector<HTMLElement>("h1");
    if (!heading) return;
    if (heading.tabIndex < 0) {
      heading.focus({ preventScroll: true });
    }
  }, [location.pathname, urgentOpen]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (urgentOpen && !dialog.open) {
      dialog.showModal();
    } else if (!urgentOpen && dialog.open) {
      dialog.close();
    }
  }, [urgentOpen]);

  return (
    <div className="app-shell" lang={languageCode} dir={dir}>
      <a className="skip-link" href="#main">
        {translate("chrome.skip_to_content")}
      </a>

      <nav className="top-chrome">
        <Link className="chrome-link" to="/exit">
          {translate("chrome.quick_exit")}
        </Link>
        <button
          type="button"
          className="chrome-link chrome-button"
          onClick={() => setUrgentOpen(true)}
        >
          {translate("chrome.urgent_help")}
        </button>
      </nav>

      {!isHome ? (
        <header className="app-header">
          <p className="brand">
            <BrandMark />
          </p>
        </header>
      ) : null}

      <main id="main" className={`app-main${isHome ? " app-main--home" : ""}`}>
        <TranslationPreviewNotice />
        <Outlet />
      </main>

      <footer className="app-footer">
        <Link to="/privacy">{translate("chrome.privacy_link")}</Link>
        {isScaffoldMode ? (
          <span className="muted">{translate("scaffold.placeholder")}</span>
        ) : null}
      </footer>

      <dialog
        ref={dialogRef}
        className="urgent-dialog"
        aria-labelledby={titleId}
        onClose={() => setUrgentOpen(false)}
      >
        <h2 id={titleId}>{translate("chrome.urgent_help.title")}</h2>
        <p>{translate("chrome.urgent_help.body")}</p>
        <div className="urgent-actions">
          <Link className="chrome-link" to="/exit">
            {translate("chrome.quick_exit")}
          </Link>
          <button
            type="button"
            className="mode-link mode-link--quiet"
            onClick={() => setUrgentOpen(false)}
          >
            {translate("chrome.urgent_help.close")}
          </button>
        </div>
      </dialog>
    </div>
  );
}
